import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Card,
  Button,
  Dialog,
  Paper,
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateSubjectMutation,
  useGetallGradeQuery,
} from "../../../Api/attoDeskApi";
import { IGrade, ISubject } from "../../../Api/Interface/api.interface";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import LoadingDialog from "../../../Components/LoadingDialog/LoadingDialog";
import BackDrop from "../../../Components/Loader/BackDrop";

interface ITeampopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: ISubject;
  onSuccess?: (newParent: { id: number }) => void;
  get_grade_id?: number | null;
}

const ItemList = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
  borderRadius: 8,
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxShadow: "none",

  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.background.paper,

  color: selected ? "#ffffff" : theme.palette.text.primary,

  "& .MuiTypography-root": {
    color: selected ? "#ffffff" : theme.palette.text.primary,
  },

  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : theme.palette.action.hover,
  },
}));

const Team = ({
  openModel = false,
  handleCloseDialog,
  data,
  onSuccess,
  get_grade_id,
}: ITeampopup) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  useEffect(() => {
    if (get_grade_id !== null && get_grade_id !== undefined) {
      setSelectedCategories((prev) =>
        prev.includes(get_grade_id) ? prev : [...prev, get_grade_id]
      );
    }
  }, [get_grade_id]);

  const [newCompany, { isLoading }] = useCreateSubjectMutation();
  const isMobileRes = useIsMobile();

  const {
    data: gradeData,
    isLoading: gradeIsLoading,
    isError,
    refetch,
  } = useGetallGradeQuery({
    searchText: "",
  });

  const handleClose = () => {
    handleCloseDialog(false);
  };

  useEffect(() => {
    if (data?.grade_ids) {
      setSelectedCategories(data.grade_ids);
    }
  }, [data]);

  const gradeList: IGrade[] = Array.isArray(gradeData?.data)
    ? (gradeData?.data as IGrade[])
    : [];

  const formik = useFormik<ISubject>({
    initialValues: {
      id: Number(data?.id),
      subject_name: data?.subject_name || "",
      memo: data?.memo || "",
      grade_ids: data?.grade_ids || [],
      perticular_grade_id: get_grade_id,
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          ...(data?.id ? { id: Number(values.id) } : {}),
          subject_name: toProperCase(values.subject_name),
          memo: values.memo || null,
          grade_ids: selectedCategories,
          perticular_grade_id: get_grade_id,
        };

        const response = await newCompany(updatedData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);

          const newParent = { id: response.getGradeSubjectId.id };

          if (newParent.id && onSuccess) {
            onSuccess(newParent);
          }
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleCategoryChange = (categoryId: number) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const formValid = useMemo(() => {
    return formik.values.subject_name === "" ||
      formik.values.subject_name === undefined
      ? false
      : true;
  }, [formik]);

  if (gradeIsLoading)
    return (
      <LoadingDialog
        open={gradeIsLoading}
        title="Loading Data"
        message="Fetching grades, please wait..."
      />
    );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="md"
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid item pt={2} alignContent={"center"}>
                <Typography className={classes.cardTitle}>
                  {data ? "Edit Subject " : "Create Subject"}
                </Typography>
              </Grid>
              <Grid
                item
                pt={2}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  className={classes.editButton}
                  onClick={handleClose}
                >
                  <CloseIcon />
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={2}>
                        <Grid item lg={12} md={12} sm={6} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Subject
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Subject"
                                size="small"
                                {...formik.getFieldProps("subject_name")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">Memo</Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Add a note about this subject (optional)"
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                {...formik.getFieldProps("memo")}
                                className={classes.textField}
                                InputProps={{ sx: { fontSize: 14 } }}
                                InputLabelProps={{ sx: { fontSize: 14 } }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={2}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Select Classs
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container spacing={1}>
                                {gradeList.map((item: IGrade) => {
                                  const isSelected =
                                    selectedCategories.includes(item.id);

                                  return (
                                    <Grid item key={item.id}>
                                      <ItemList
                                        selected={isSelected}
                                        onClick={() =>
                                          handleCategoryChange(item.id)
                                        }
                                        sx={{
                                          background: isSelected
                                            ? theme.palette.primary.main
                                            : "#eef2f7",

                                          color: isSelected
                                            ? "#ffffff"
                                            : theme.palette.primary.main,
                                        }}
                                      >
                                        <Typography variant="body2">
                                          {item.grade || "Undefined"}
                                        </Typography>
                                      </ItemList>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Button
                          variant="contained"
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                          disabled={!formValid || isLoading}
                        >
                          {isLoading && (
                            <CircularProgress
                              size={18}
                              className={classes.isLoading}
                              sx={{ marginRight: 1 }}
                            />
                          )}
                          {data ? "Update" : "Create"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormikProvider>
            {isLoading && <BackDrop open={isLoading} />}
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Team;
